import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Alert,
  Chip,
} from '@mui/material';
import {useGetSeedPath} from '../../../hooks/productions/useProductions';
import {GetSeedPathRequestParam} from '../../../api/types/production.type';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import '../Demo.scss';

const GetSeedPathApiTest: React.FC = () => {
  const [documentId, setDocumentId] = useState<number>(28);
  const [productionId, setProductionId] = useState<number>(54);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const params: GetSeedPathRequestParam = {
    documentId,
    productionId,
  };

  const {data, isLoading, error, refetch} = useGetSeedPath(params, enabled);

  const handleTestApi = () => {
    setEnabled(true);
    refetch();
  };

  const handleReset = () => {
    setDocumentId(28);
    setProductionId(54);
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (data) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleCopyRequest = async () => {
    const request = {
      documentId,
      productionId,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(request, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        📁 Get Seed Path API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Get the seed file path for a document in a production
      </Typography>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Seed Path
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/seed-path?documentId={documentId}&productionId={productionId}
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get the seed file path for a specific document in a production.
            Returns success status, seed file path, and page count.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Parameters */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Document ID"
            type="number"
            value={documentId}
            onChange={(e) => setDocumentId(Number(e.target.value))}
            size="small"
            placeholder="Enter document ID"
            sx={{minWidth: '200px'}}
          />
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
            placeholder="Enter production ID"
            sx={{minWidth: '200px'}}
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            disabled={isLoading}
          >
            {isLoading ? '⏳ Loading...' : '🚀 Get Seed Path'}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            🔄 Reset
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Loading seed path... Making API request...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {error.message}
          </Typography>
        </Box>
      )}

      {/* Success State - Display Response */}
      {data && !isLoading && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* Success Message */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size" gutterBottom>
              🎉 Seed Path Retrieved Successfully!
            </Typography>
            <Typography className="typo-text body3_size" gutterBottom>
              <strong>Success:</strong> {data.success ? '✅ Yes' : '❌ No'}
            </Typography>
            <Typography className="typo-text body3_size" gutterBottom>
              <strong>Seed File Path:</strong>
            </Typography>
            <Box
              sx={{
                backgroundColor: '#fff',
                padding: 1,
                borderRadius: 1,
                marginTop: 1,
                marginBottom: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all',
              }}
            >
              {data.seedFilePath}
            </Box>
            <Typography className="typo-text body3_size">
              <strong>Page Count:</strong> {data.pageCount}
            </Typography>
          </Box>

          {/* Expandable Sections */}
          <Box sx={{marginTop: 2}}>
            {/* Request Parameters */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📤 Request Parameters
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyRequest}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Request'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    {
                      documentId,
                      productionId,
                    },
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Response Data */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📥 Response Data
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyJson}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Response'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(data, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Full Response */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📄 Full JSON Response
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: '#263238',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '500px',
                  }}
                >
                  <pre
                    style={{
                      color: '#4caf50',
                      fontSize: '12px',
                      margin: 0,
                      fontFamily: 'monospace',
                    }}
                  >
                    {JSON.stringify(data, null, 2)}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      )}

      {/* Initial State */}
      {!isLoading && !data && !error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ff9800',
          }}
        >
          <Typography className="typo-text body2_size">
            👆 Fill in the parameters and click "Get Seed Path" button to make
            the API request
          </Typography>
        </Box>
      )}

      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/seed-path
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> GET
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Query Parameters:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`documentId=${documentId}&productionId=${productionId}`}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Expected Response:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`{
  "success": true,
  "seedFilePath": "string",
  "pageCount": 1
}`}
        </pre>
      </Box>

      {/* Hook Usage Example */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#f0f8ff'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          💻 Hook Usage Example
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {`import { useGetSeedPath } from '../../../hooks/productions/useProductions';

const MyComponent = () => {
  const { data, isLoading, error } = useGetSeedPath(
    {
      documentId: 28,
      productionId: 54
    },
    true // enabled
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!data) return null;

  return (
    <div>
      <p>Success: {data.success ? 'Yes' : 'No'}</p>
      <p>Seed File Path: {data.seedFilePath}</p>
      <p>Page Count: {data.pageCount}</p>
    </div>
  );
};`}
        </pre>
      </Box>
    </Box>
  );
};

export default GetSeedPathApiTest;

